/* eslint-disable @typescript-eslint/no-invalid-void-type */
/**
 * Auth, two-factor, sessions and profile/avatar hooks.
 * Split out of the original monolithic `react.ts`.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApiClient } from '../context';
import { ApiError } from '../hc';
import { deleteJson, getJson, patchJson, postJson } from './_http';

/* ─── Shared response shapes (mirror apps/api/src/auth.ts) ──────────────── */

export interface AuthUser {
  id: string;
  email: string;
  name?: string | null;
  emailVerified: boolean;
  image?: string | null;
  createdAt: string;
  updatedAt: string;
  twoFactorEnabled?: boolean;
  [key: string]: unknown;
}

export interface AuthSession {
  id: string;
  userId: string;
  /** Better Auth session token — the revocation handle for `useRevokeSession`. */
  token: string;
  expiresAt: string;
  ipAddress?: string | null;
  userAgent?: string | null;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export interface MeResponse {
  user: AuthUser;
  session: AuthSession;
  /**
   * Better Auth flag: when truthy, the email/password match succeeded but
   * the account has TOTP 2FA enabled. The session is intentionally
   * partial — the client must POST `/two-factor/verify-totp` with the
   * 6-digit code to upgrade to a full session.
   */
  twoFactorRedirect?: boolean;
}

/** `POST /api/v1/auth/two-factor/verify-totp` — challenge step at login. */
export function use2faChallenge(): UseMutationResult<
  { success: true },
  ApiError,
  { code: string; trustDevice?: boolean }
> {
  // Hook ordering — defined here so LoginPage can import it next to
  // `useSignIn`. Implementation lives near the existing `use2fa*` hooks but
  // we forward via an alias-style wrapper so the file structure stays
  // tree-shake-friendly.
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ success: true }, ApiError, { code: string; trustDevice?: boolean }>({
    mutationFn: (vars) =>
      postJson<{ success: true }>(client, '/api/v1/auth/two-factor/verify-totp', vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export interface SignInParams {
  email: string;
  password: string;
  rememberMe?: boolean;
  callbackURL?: string;
}

export interface SignUpParams {
  email: string;
  password: string;
  name: string;
  locale?: string;
}

export interface ForgotPasswordParams {
  email: string;
  redirectTo?: string;
}

export interface ResetPasswordParams {
  token: string;
  newPassword: string;
}

export interface VerifyEmailParams {
  token: string;
}

export interface ChangePasswordParams {
  currentPassword: string;
  newPassword: string;
  revokeOtherSessions?: boolean;
}

export interface ChangeEmailParams {
  newEmail: string;
  callbackURL?: string;
}

export interface DeleteAccountParams {
  /** Required when the user has a password account (Better Auth). */
  password?: string;
  /** Optional callback URL after delete (BA convention). */
  callbackURL?: string;
}

export interface UpdateProfileParams {
  name?: string;
  uiLang?: 'fr' | 'en';
  timezone?: string;
  themeMode?: 'dark' | 'light' | 'system';
  reduceMotion?: boolean;
  inappNotifications?: boolean;
}

export interface TwoFactorEnrollResponse {
  secret: string;
  /** `otpauth://…` URL. */
  totpURI: string;
  /** PNG data-URL of the QR code (when the API renders one). */
  qrCodeDataUrl?: string;
  /**
   * One-time backup codes returned by Better Auth on enrollment. Wave B6
   * surfaces these to the user (copyable + downloadable) per US-009.
   */
  backupCodes?: string[];
}

export interface TwoFactorBackupCodesResponse {
  backupCodes: string[];
}

export interface TwoFactorVerifyParams {
  code: string;
  trustDevice?: boolean;
}

export interface TwoFactorDisableParams {
  password: string;
  code?: string;
}
/* ─── Auth — Wave 2.2 (FULLY IMPLEMENTED) ───────────────────────────────── */

const ME_MAX_RETRIES = 2;

/**
 * Retry policy for `useMe`. Retries *transient* transport faults (5xx, or a
 * network-level failure with no parsed envelope) but never a 4xx — a
 * 401/403 is an authoritative auth verdict, and retrying it would only delay
 * the session-expiry handling. Mirrors `apps/web/src/lib/queryClient.ts`'s
 * `shouldRetry`. The point is that a one-off backend blip (worker cold-start,
 * a dropped request during fast navigation) must not surface as a spurious
 * logout — it self-heals on retry, while a genuine 401 still settles at once.
 */
function retryTransient(failureCount: number, error: unknown): boolean {
  if (failureCount >= ME_MAX_RETRIES) return false;
  if (error instanceof ApiError) return error.httpStatus >= 500;
  return true;
}

/**
 * `GET /api/v1/auth/me` — current user.
 *
 * Returns `undefined` while loading; the resolved data shape is
 * `{ user, session }`. Errors propagate as `ApiError`. The query is
 * disabled by default if you pass `enabled: false` (e.g. when the route
 * already knows the user is unauthenticated).
 *
 * Defaults to {@link retryTransient}: transient faults retry, 401/403 settle
 * immediately. Pass `retry: false` to opt out (e.g. one-shot probes).
 */
export function useMe(
  options: {
    enabled?: boolean;
    retry?: number | boolean | ((failureCount: number, error: ApiError) => boolean);
  } = {},
): UseQueryResult<MeResponse, ApiError> {
  const client = useApiClient();
  return useQuery<MeResponse, ApiError>({
    queryKey: ['me'],
    queryFn: () => getJson<MeResponse>(client, '/api/v1/auth/me'),
    enabled: options.enabled,
    retry: options.retry ?? retryTransient,
  });
}

/** `POST /api/v1/auth/sign-up/email` — create account. */
export function useSignUp(): UseMutationResult<MeResponse, ApiError, SignUpParams> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<MeResponse, ApiError, SignUpParams>({
    mutationFn: (vars) => postJson<MeResponse>(client, '/api/v1/auth/sign-up/email', vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/** `POST /api/v1/auth/sign-in/email` — start a session. */
export function useSignIn(): UseMutationResult<MeResponse, ApiError, SignInParams> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<MeResponse, ApiError, SignInParams>({
    mutationFn: (vars) => postJson<MeResponse>(client, '/api/v1/auth/sign-in/email', vars),
    onSuccess: () => {
      // Wipe the entire cache — anything cached before sign-in belongs to a
      // different (or orphan) identity. Symmetrical with `useSignOut`.
      qc.clear();
    },
  });
}

/** `POST /api/v1/auth/sign-out` — end the current session. */
export function useSignOut(): UseMutationResult<{ success: true }, ApiError, void> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ success: true }, ApiError, void>({
    mutationFn: () => postJson<{ success: true }>(client, '/api/v1/auth/sign-out', {}),
    onSuccess: () => {
      qc.clear();
    },
  });
}

/** `POST /api/v1/auth/revoke-sessions` — revoke every session for the user. */
export function useSignOutAll(): UseMutationResult<{ success: true }, ApiError, void> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ success: true }, ApiError, void>({
    mutationFn: () => postJson<{ success: true }>(client, '/api/v1/auth/revoke-sessions', {}),
    onSuccess: () => {
      qc.clear();
    },
  });
}

/**
 * `POST /api/v1/auth/request-password-reset` — request a password-reset
 * link (Better Auth 1.6.x canonical name; `/forget-password` no longer
 * exists). Always 200 (anti-enumeration); errors only surface for
 * transport faults or rate-limit violations.
 */
export function useForgotPassword(): UseMutationResult<
  { success: true },
  ApiError,
  ForgotPasswordParams
> {
  const client = useApiClient();
  return useMutation<{ success: true }, ApiError, ForgotPasswordParams>({
    mutationFn: (vars) =>
      postJson<{ success: true }>(client, '/api/v1/auth/request-password-reset', vars),
  });
}

/**
 * `POST /api/v1/auth/reset-password` — finalize a reset.
 * On success, all sessions are revoked → clear the entire query cache.
 */
export function useResetPassword(): UseMutationResult<
  { success: true },
  ApiError,
  ResetPasswordParams
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ success: true }, ApiError, ResetPasswordParams>({
    mutationFn: (vars) => postJson<{ success: true }>(client, '/api/v1/auth/reset-password', vars),
    onSuccess: () => {
      qc.clear();
    },
  });
}

/**
 * `GET /api/v1/auth/verify-email?token=…` — confirm an email address from
 * a token. Better Auth only exposes a GET route (`token` + optional
 * `callbackURL` query params). We deliberately omit `callbackURL`: without
 * it BA answers with JSON (`{ status: true, user }`) instead of a 302, so
 * the web app keeps full control over UI + analytics.
 */
export function useVerifyEmail(): UseMutationResult<
  { success: true },
  ApiError,
  VerifyEmailParams
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ success: true }, ApiError, VerifyEmailParams>({
    mutationFn: (vars) =>
      getJson<{ success: true }>(
        client,
        `/api/v1/auth/verify-email?token=${encodeURIComponent(vars.token)}`,
      ),
    onSuccess: () => {
      // BA's `autoSignInAfterVerification: true` issues a new session; treat
      // this like sign-in and drop any cache that pre-dates the new identity.
      qc.clear();
    },
  });
}

/** `POST /api/v1/auth/send-verification-email` — re-send the verify link. */
export function useResendVerification(): UseMutationResult<
  { success: true },
  ApiError,
  { email: string }
> {
  const client = useApiClient();
  return useMutation<{ success: true }, ApiError, { email: string }>({
    mutationFn: (vars) =>
      postJson<{ success: true }>(client, '/api/v1/auth/send-verification-email', vars),
  });
}

/** `POST /api/v1/auth/change-password` — change while signed in. */
export function useChangePassword(): UseMutationResult<
  { success: true },
  ApiError,
  ChangePasswordParams
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ success: true }, ApiError, ChangePasswordParams>({
    mutationFn: (vars) => postJson<{ success: true }>(client, '/api/v1/auth/change-password', vars),
    onSuccess: (_data, variables) => {
      // When `revokeOtherSessions` is true, BA rotates the session — refresh
      // both `me` and `sessions` so the UI updates.
      if (variables.revokeOtherSessions) {
        void qc.invalidateQueries({ queryKey: ['me'] });
        void qc.invalidateQueries({ queryKey: ['sessions'] });
      }
    },
  });
}

/**
 * `POST /api/v1/auth/change-email` — request an email change (US-188).
 *
 * Better Auth's two-step flow:
 *   1. The current (verified) user POSTs `{ newEmail }`. BA emits a
 *      confirmation email to the OLD address.
 *   2. The user clicks the OLD-address link, which makes BA send a
 *      verification email to the NEW address.
 *   3. Clicking the NEW-address link finalises the swap.
 *
 * The hook returns immediately after step 1 — UI surfaces a "pending"
 * state and prompts the user to check both inboxes.
 */
export function useChangeEmail(): UseMutationResult<{ status: true }, ApiError, ChangeEmailParams> {
  const client = useApiClient();
  return useMutation<{ status: true }, ApiError, ChangeEmailParams>({
    mutationFn: (vars) => postJson<{ status: true }>(client, '/api/v1/auth/change-email', vars),
  });
}

/**
 * `POST /api/v1/auth/delete-user` — destroy the account (US-014).
 * (Better Auth registers this as POST, not DELETE.)
 *
 * Better Auth requires the current password (when present) as a
 * confirmation. After success the cookie is cleared; consumers should
 * navigate to the sign-up / marketing surface.
 */
export function useDeleteAccount(): UseMutationResult<
  { success: true },
  ApiError,
  DeleteAccountParams
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ success: true }, ApiError, DeleteAccountParams>({
    mutationFn: (vars) => postJson<{ success: true }>(client, '/api/v1/auth/delete-user', vars),
    onSuccess: () => {
      qc.clear();
    },
  });
}

/**
 * `PATCH /api/v1/me` — update the editable subset of the user's profile.
 *
 * Wave 5a Agent 5a.3. Email changes go through `useChangeEmail` (US-188).
 * Avatar changes go through the presigned-URL flow.
 */
export function useUpdateProfile(): UseMutationResult<
  { user: AuthUser },
  ApiError,
  UpdateProfileParams
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ user: AuthUser }, ApiError, UpdateProfileParams>({
    mutationFn: (vars) => patchJson<{ user: AuthUser }>(client, '/api/v1/me', vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/** `DELETE /api/v1/me/avatar` — clear the user's avatar. */
export function useDeleteAvatar(): UseMutationResult<{ ok: true }, ApiError, void> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, void>({
    mutationFn: () => deleteJson<{ ok: true }>(client, '/api/v1/me/avatar'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export interface AvatarSignUploadParams {
  contentType: string;
}
export interface AvatarSignUploadResponse {
  uploadUrl: string;
  key: string;
  expiresAt: string;
  maxBytes: number;
}

/**
 * `POST /api/v1/me/avatar/sign-upload` — get a presigned R2 PUT URL for
 * the avatar upload pipeline (US-182). Caller PUTs the file to `uploadUrl`
 * directly, then calls `useCompleteAvatar` with the returned `key`.
 */
export function useSignAvatarUpload(): UseMutationResult<
  AvatarSignUploadResponse,
  ApiError,
  AvatarSignUploadParams
> {
  const client = useApiClient();
  return useMutation<AvatarSignUploadResponse, ApiError, AvatarSignUploadParams>({
    mutationFn: (vars) =>
      postJson<AvatarSignUploadResponse>(client, '/api/v1/me/avatar/sign-upload', vars),
  });
}

/**
 * `POST /api/v1/me/avatar/complete` — finalise an R2 upload. Validates
 * size + magic bytes server-side, persists `users.image`. Returns the
 * public URL of the new avatar.
 */
export function useCompleteAvatar(): UseMutationResult<{ url: string }, ApiError, { key: string }> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ url: string }, ApiError, { key: string }>({
    mutationFn: (vars) => postJson<{ url: string }>(client, '/api/v1/me/avatar/complete', vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/**
 * `POST /api/v1/auth/two-factor/enable` — enroll TOTP. Better Auth returns
 * only `{ totpURI, backupCodes }`; the manual-entry `secret` consumed by
 * `TwoFactorSetup` is derived here from the otpauth URI's `secret=` query
 * param so the response shape callers see stays `TwoFactorEnrollResponse`.
 */
export function use2faEnroll(): UseMutationResult<
  TwoFactorEnrollResponse,
  ApiError,
  { password: string }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<TwoFactorEnrollResponse, ApiError, { password: string }>({
    mutationFn: async (vars) => {
      const raw = await postJson<Omit<TwoFactorEnrollResponse, 'secret'> & { secret?: string }>(
        client,
        '/api/v1/auth/two-factor/enable',
        vars,
      );
      let secret = raw.secret ?? '';
      if (!secret && raw.totpURI) {
        try {
          secret = new URL(raw.totpURI).searchParams.get('secret') ?? '';
        } catch {
          /* malformed URI — leave manual-entry secret empty, QR fallback still works */
        }
      }
      return { ...raw, secret };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/**
 * `POST /api/v1/auth/two-factor/verify-totp` — confirm a TOTP code.
 * Better Auth has no `/two-factor/verify` route; `verify-totp` covers both
 * the post-enroll setup confirmation (flips `twoFactorEnabled` on first
 * valid code) and the login challenge (see `use2faChallenge`).
 */
export function use2faVerify(): UseMutationResult<
  { success: true },
  ApiError,
  TwoFactorVerifyParams
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ success: true }, ApiError, TwoFactorVerifyParams>({
    mutationFn: (vars) =>
      postJson<{ success: true }>(client, '/api/v1/auth/two-factor/verify-totp', vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/** `POST /api/v1/auth/two-factor/disable` — turn TOTP off. */
export function use2faDisable(): UseMutationResult<
  { success: true },
  ApiError,
  TwoFactorDisableParams
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ success: true }, ApiError, TwoFactorDisableParams>({
    mutationFn: (vars) =>
      postJson<{ success: true }>(client, '/api/v1/auth/two-factor/disable', vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/**
 * `POST /api/v1/auth/two-factor/regenerate-backup-codes` — replace the
 * existing backup codes with a fresh set. Returns the new codes; old codes
 * stop working.
 */
export function useRegenerateBackupCodes(): UseMutationResult<
  TwoFactorBackupCodesResponse,
  ApiError,
  { password: string }
> {
  const client = useApiClient();
  return useMutation<TwoFactorBackupCodesResponse, ApiError, { password: string }>({
    mutationFn: (vars) =>
      postJson<TwoFactorBackupCodesResponse>(
        client,
        '/api/v1/auth/two-factor/regenerate-backup-codes',
        vars,
      ),
  });
}

/**
 * `GET /api/v1/auth/list-sessions` — list all active sessions for the
 * user. Better Auth returns a bare `Session[]`; we wrap it in
 * `{ sessions }` to keep the hook's envelope stable for consumers.
 */
export function useSessions(
  options: { enabled?: boolean } = {},
): UseQueryResult<{ sessions: AuthSession[] }, ApiError> {
  const client = useApiClient();
  return useQuery<{ sessions: AuthSession[] }, ApiError>({
    queryKey: ['sessions'],
    queryFn: async () => ({
      sessions: await getJson<AuthSession[]>(client, '/api/v1/auth/list-sessions'),
    }),
    enabled: options.enabled,
    retry: false,
  });
}

/**
 * `POST /api/v1/auth/revoke-session` — revoke a specific session.
 * Better Auth revokes by session *token* (not id) — pass the `token`
 * field from the `useSessions` list.
 */
export function useRevokeSession(): UseMutationResult<
  { success: true },
  ApiError,
  { token: string }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ success: true }, ApiError, { token: string }>({
    mutationFn: (vars) => postJson<{ success: true }>(client, '/api/v1/auth/revoke-session', vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

/** `POST /api/v1/auth/revoke-other-sessions` — preserve the current session. */
export function useRevokeOtherSessions(): UseMutationResult<{ success: true }, ApiError, void> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ success: true }, ApiError, void>({
    mutationFn: () => postJson<{ success: true }>(client, '/api/v1/auth/revoke-other-sessions', {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

// ─── Aliases — Wave 5a Agent 5a.3 ──────────────────────────────────────────
//
// The Settings surface refers to the 2FA + sessions hooks by friendlier
// names; we expose both the historical (`use2faEnroll`) and the new
// (`useEnable2FA`) so existing call-sites keep working while the Settings
// agent uses the verb-first shape.

export const useEnable2FA = use2faEnroll;
export const useVerify2FA = use2faVerify;
export const useDisable2FA = use2faDisable;
export const useListSessions = useSessions;
